import React, { useMemo } from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Gauge, TrendingUp } from 'lucide-react';
import { collectContentImages, resolveEffectiveOgImage } from '@/lib/ogImage';

/**
 * SEOScoreCard — industry-standard SEO health score (0-100) for staff.
 *
 * Modeled on modern 2025-2026 SEO scoring frameworks used by Yoast, RankMath,
 * SEMrush, Ahrefs and Google's own Search Essentials. Each check maps to a
 * weighted point contribution so scores are stable and comparable across
 * blogs, static pages, categories, and listings.
 *
 * Props:
 *   values:     the same object shape used by SEOFieldsBlock (focus_keyword,
 *               meta_title, meta_description, canonical_url, og_image_url,
 *               slug, title, excerpt/description, html_content).
 *   contentType: 'blog' | 'page' | 'category' | 'listing' — tunes checks and
 *                thresholds (e.g. blogs demand ≥600 words, listings ≥60).
 *   className:  additional wrapper classes.
 *
 * Staff-facing only. The parent (SEOFieldsBlock) gates visibility by role.
 */

// Strip HTML tags to get plain text for word-count / density checks.
const stripHtml = (html = '') => {
  if (!html) return '';
  // Replace block boundaries with spaces to preserve word separation.
  return html
    .replace(/<\/(p|div|li|h[1-6]|br|tr|td|th)>/gi, ' ')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const wordCount = (text) => (text ? text.split(/\s+/).filter(Boolean).length : 0);

const countOccurrences = (haystack, needle) => {
  if (!haystack || !needle) return 0;
  const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const matches = haystack.match(new RegExp(`\\b${escaped}\\b`, 'gi'));
  return matches ? matches.length : 0;
};

// Extract first N chars of visible text — used for "keyword in first paragraph" check.
const firstParagraph = (html) => {
  const m = html && html.match(/<p[^>]*>([\s\S]*?)<\/p>/i);
  if (m) return stripHtml(m[1]);
  return stripHtml(html).slice(0, 400);
};

// Average sentence length across the plain-text body — proxy for readability.
const avgSentenceLength = (text) => {
  if (!text) return 0;
  const sentences = text.split(/[.!?]+/).map(s => s.trim()).filter(s => s.split(/\s+/).length > 2);
  if (!sentences.length) return 0;
  const totalWords = sentences.reduce((sum, s) => sum + s.split(/\s+/).filter(Boolean).length, 0);
  return Math.round(totalWords / sentences.length);
};

// Content-type presets. Each key marks whether the check applies + its weight.
const PRESETS = {
  blog: {
    minWords: 600,
    idealWords: 1200,
    checkInternalLinks: true,
    checkExternalLinks: true,
    checkImages: true,
    checkKeywordDensity: true,
    checkHeadings: true,
  },
  page: {
    minWords: 300,
    idealWords: 600,
    checkInternalLinks: false,
    checkExternalLinks: false,
    checkImages: true,
    checkKeywordDensity: false,
    checkHeadings: true,
  },
  category: {
    minWords: 80,
    idealWords: 200,
    checkInternalLinks: false,
    checkExternalLinks: false,
    checkImages: false,
    checkKeywordDensity: false,
    checkHeadings: false,
  },
  listing: {
    minWords: 60,
    idealWords: 150,
    checkInternalLinks: false,
    checkExternalLinks: false,
    checkImages: true,
    checkKeywordDensity: false,
    checkHeadings: false,
  },
};

/**
 * Runs all applicable checks and returns { score, checks[], breakdown }.
 * Each check has: { key, label, weight, status: 'pass'|'warn'|'fail', tip }.
 */
export const computeSeoScore = (values = {}, contentType = 'page') => {
  const preset = PRESETS[contentType] || PRESETS.page;
  const kwRaw = (values.focus_keyword || '').trim();
  const kw = kwRaw.toLowerCase();
  const title = (values.meta_title || values.title || '').trim();
  const titleLower = title.toLowerCase();
  const meta = (values.meta_description || '').trim();
  const metaLower = meta.toLowerCase();
  const slug = (values.slug || '').toLowerCase();
  // Rich-text body. Blogs/static/location pages keep it in `html_content`;
  // categories, subcategories and section landing pages split it across
  // `intro_html` + `content_html`, so analyse BOTH together (otherwise the
  // word count / keyword checks only ever saw the short `description`).
  const html = values.html_content
    || values.content
    || [values.intro_html, values.content_html].filter(Boolean).join('\n')
    || '';
  // Body text: rich-text pages use html_content; plain-text entities (listings,
  // categories) keep their body in `description`/`excerpt`. Fall back to those
  // so word-count / keyword-in-body checks work everywhere.
  const plainBody = (values.description || values.excerpt || '').trim();
  const bodyText = stripHtml(html) || plainBody;
  const bodyLower = bodyText.toLowerCase();
  const words = wordCount(bodyText);
  const excerpt = (values.excerpt || values.description || '').toLowerCase();
  const firstPara = (firstParagraph(html) || bodyText.slice(0, 400)).toLowerCase();

  const checks = [];

  // ————————————————————————————————————————————
  // Focus keyword group (highest weight — the SEO north-star)
  // ————————————————————————————————————————————
  if (!kw) {
    checks.push({
      key: 'kw_present', label: 'Focus keyword set', weight: 10,
      status: 'fail', tip: 'Set a focus keyword — the primary phrase you want this page to rank for.',
    });
  } else {
    checks.push({
      key: 'kw_present', label: 'Focus keyword set', weight: 10,
      status: 'pass', tip: `Focus keyword: “${kwRaw}”.`,
    });

    // Title contains keyword — ideally at the start
    const titleHasKw = titleLower.includes(kw);
    const titleStartsWithKw = titleLower.startsWith(kw);
    checks.push({
      key: 'kw_in_title',
      label: titleStartsWithKw ? 'Keyword at start of meta title' : 'Keyword in meta title',
      weight: 10,
      status: titleStartsWithKw ? 'pass' : (titleHasKw ? 'warn' : 'fail'),
      tip: titleStartsWithKw
        ? 'Excellent — Google weighs early keywords higher.'
        : (titleHasKw
          ? 'Try moving the focus keyword to the start of the title for stronger relevance.'
          : 'Add the focus keyword to the meta title.'),
    });

    // Meta description contains keyword
    const metaHasKw = metaLower.includes(kw) || excerpt.includes(kw);
    checks.push({
      key: 'kw_in_meta', label: 'Keyword in meta description', weight: 6,
      status: metaHasKw ? 'pass' : 'fail',
      tip: metaHasKw ? 'Focus keyword appears in the meta description.' : 'Include the focus keyword naturally in the meta description.',
    });

    // Slug contains keyword
    const kwSlug = kw.replace(/\s+/g, '-');
    const slugHasKw = slug.includes(kwSlug);
    checks.push({
      key: 'kw_in_slug', label: 'Keyword in URL slug', weight: 6,
      status: slugHasKw ? 'pass' : (slug ? 'warn' : 'fail'),
      tip: slugHasKw ? 'Keyword-rich slug looks good.' : 'A short, keyword-containing slug ranks better.',
    });

    // Keyword in body
    const kwCount = countOccurrences(bodyLower, kw);
    const bodyHasKw = kwCount > 0;
    checks.push({
      key: 'kw_in_body', label: 'Keyword in body content', weight: 6,
      status: bodyHasKw ? 'pass' : 'fail',
      tip: bodyHasKw ? `Focus keyword appears ${kwCount}× in the body.` : 'Mention the focus keyword at least once in the body copy.',
    });

    // Keyword in first paragraph
    const kwInFirstPara = firstPara.includes(kw);
    checks.push({
      key: 'kw_in_first_para', label: 'Keyword in first paragraph', weight: 4,
      status: kwInFirstPara ? 'pass' : 'warn',
      tip: kwInFirstPara ? 'Great — early keyword mention signals relevance.' : 'Introduce the focus keyword in the opening paragraph.',
    });

    // Keyword in H1/H2/H3 headings
    if (preset.checkHeadings) {
      const headingsMatch = html.match(/<h[1-3][^>]*>([\s\S]*?)<\/h[1-3]>/gi) || [];
      const headingText = headingsMatch.map(h => stripHtml(h)).join(' ').toLowerCase();
      const kwInHeadings = headingText.includes(kw);
      checks.push({
        key: 'kw_in_headings', label: 'Keyword in a subheading', weight: 4,
        status: kwInHeadings ? 'pass' : (headingsMatch.length ? 'warn' : 'fail'),
        tip: kwInHeadings ? 'Focus keyword found in a subheading.' : 'Add the focus keyword to at least one H2 or H3.',
      });
    }

    // Keyword density (blogs only — 0.5% to 2.5% ideal, higher = stuffing)
    if (preset.checkKeywordDensity && words > 0) {
      const density = (kwCount / words) * 100;
      let status = 'fail';
      let tip = `Density is ${density.toFixed(2)}%. Aim for 0.5%-2.5%.`;
      if (density >= 0.5 && density <= 2.5) { status = 'pass'; tip = `Healthy keyword density: ${density.toFixed(2)}%.`; }
      else if (density > 2.5 && density <= 4) { status = 'warn'; tip = `Density ${density.toFixed(2)}% is a bit high — risks keyword stuffing.`; }
      else if (density > 4) { status = 'fail'; tip = `Density ${density.toFixed(2)}% is too high (keyword stuffing).`; }
      else if (density > 0 && density < 0.5) { status = 'warn'; tip = `Density ${density.toFixed(2)}% is low — mention the keyword a few more times naturally.`; }
      checks.push({ key: 'kw_density', label: 'Keyword density', weight: 4, status, tip });
    }
  }

  // ————————————————————————————————————————————
  // Meta title length (Google shows ~60 chars, hard cap ~70)
  // ————————————————————————————————————————————
  const titleLen = title.length;
  let titleStatus = 'fail';
  let titleTip = 'Add a meta title (50-60 characters).';
  if (titleLen >= 50 && titleLen <= 60) { titleStatus = 'pass'; titleTip = `${titleLen} chars — ideal length.`; }
  else if (titleLen >= 30 && titleLen < 50) { titleStatus = 'warn'; titleTip = `${titleLen} chars — a little short. Aim for 50-60.`; }
  else if (titleLen > 60 && titleLen <= 65) { titleStatus = 'warn'; titleTip = `${titleLen} chars — Google may truncate. Ideal is ≤60.`; }
  else if (titleLen > 65) { titleStatus = 'fail'; titleTip = `${titleLen} chars — will be truncated in search results.`; }
  else if (titleLen > 0) { titleStatus = 'fail'; titleTip = `${titleLen} chars — too short to convey value.`; }
  checks.push({ key: 'title_length', label: 'Meta title length', weight: 6, status: titleStatus, tip: titleTip });

  // ————————————————————————————————————————————
  // Meta description length (140-160 sweet spot)
  // ————————————————————————————————————————————
  const metaLen = meta.length;
  let metaStatus = 'fail';
  let metaTip = 'Add a meta description (140-160 characters).';
  if (metaLen >= 140 && metaLen <= 160) { metaStatus = 'pass'; metaTip = `${metaLen} chars — perfect.`; }
  else if (metaLen >= 100 && metaLen < 140) { metaStatus = 'warn'; metaTip = `${metaLen} chars — a bit short. Expand to 140-160.`; }
  else if (metaLen > 160 && metaLen <= 165) { metaStatus = 'warn'; metaTip = `${metaLen} chars — may truncate in SERPs.`; }
  else if (metaLen > 165) { metaStatus = 'fail'; metaTip = `${metaLen} chars — will be truncated.`; }
  else if (metaLen > 0) { metaStatus = 'fail'; metaTip = `${metaLen} chars — too short.`; }
  checks.push({ key: 'meta_length', label: 'Meta description length', weight: 6, status: metaStatus, tip: metaTip });

  // ————————————————————————————————————————————
  // Content length
  // ————————————————————————————————————————————
  let contentStatus = 'fail';
  let contentTip = `${words} words. Aim for at least ${preset.minWords}.`;
  if (words >= preset.idealWords) { contentStatus = 'pass'; contentTip = `${words} words — excellent depth.`; }
  else if (words >= preset.minWords) { contentStatus = 'warn'; contentTip = `${words} words — passes minimum, but ${preset.idealWords}+ ranks better.`; }
  else if (words > 0) { contentStatus = 'fail'; contentTip = `Only ${words} words — expand to at least ${preset.minWords}.`; }
  checks.push({ key: 'content_length', label: 'Content length', weight: 8, status: contentStatus, tip: contentTip });

  // ————————————————————————————————————————————
  // Slug quality (short, hyphenated, no stopwords)
  // ————————————————————————————————————————————
  if (slug) {
    let slugStatus = 'pass';
    let slugTip = 'Slug looks clean.';
    if (slug.length > 75) { slugStatus = 'fail'; slugTip = `Slug is ${slug.length} chars — trim to under 75.`; }
    else if (slug.length > 60) { slugStatus = 'warn'; slugTip = `${slug.length} chars — could be shorter.`; }
    else if (/_|[A-Z]/.test(values.slug || '')) { slugStatus = 'warn'; slugTip = 'Use only lowercase letters and hyphens in slugs.'; }
    checks.push({ key: 'slug_quality', label: 'Slug quality', weight: 3, status: slugStatus, tip: slugTip });
  } else {
    checks.push({ key: 'slug_quality', label: 'Slug quality', weight: 3, status: 'warn', tip: 'Slug missing — one will be generated from the title.' });
  }

  // ————————————————————————————————————————————
  // Heading structure (must have at least one H2 for pages, and an H1 elsewhere)
  // ————————————————————————————————————————————
  if (preset.checkHeadings) {
    const h1Count = (html.match(/<h1\b/gi) || []).length;
    const h2Count = (html.match(/<h2\b/gi) || []).length;
    const h3Count = (html.match(/<h3\b/gi) || []).length;
    const hasHeadings = h1Count + h2Count + h3Count > 0;
    let hStatus = 'fail';
    let hTip = 'Add subheadings (H2/H3) to structure the content.';
    if (h1Count > 1) { hStatus = 'fail'; hTip = `${h1Count} H1s found — only use one H1 per page.`; }
    else if (hasHeadings && h2Count >= 1) { hStatus = 'pass'; hTip = `${h1Count ? '1 H1, ' : ''}${h2Count} H2${h2Count > 1 ? 's' : ''}, ${h3Count} H3${h3Count > 1 ? 's' : ''} — great structure.`; }
    else if (hasHeadings) { hStatus = 'warn'; hTip = 'Add at least one H2 to break up the content.'; }
    checks.push({ key: 'heading_structure', label: 'Heading structure', weight: 5, status: hStatus, tip: hTip });
  }

  // ————————————————————————————————————————————
  // Image accessibility (alt text on ≥ 80% of images)
  // Image EXISTENCE and image METADATA are judged separately: the inventory
  // spans every legitimate source for this content type (listing media,
  // blog cover, page hero, body <img>), and each image only counts as
  // covered when it carries its own non-empty alt text.
  // ————————————————————————————————————————————
  if (preset.checkImages) {
    const imgs = collectContentImages(values, contentType);
    if (imgs.length === 0) {
      checks.push({
        key: 'images_alt', label: 'Images with alt text', weight: 4,
        status: 'warn', tip: 'No images found. Adding at least one relevant image improves engagement.',
      });
    } else {
      const withAlt = imgs.filter(i => i.alt).length;
      const ratio = withAlt / imgs.length;
      let status = 'fail';
      let tip = `${withAlt}/${imgs.length} images have alt text.`;
      if (ratio === 1) { status = 'pass'; tip = `All ${imgs.length} image${imgs.length > 1 ? 's have' : ' has'} alt text.`; }
      else if (ratio >= 0.8) { status = 'warn'; tip = `${withAlt}/${imgs.length} images have alt text — cover the rest.`; }
      checks.push({ key: 'images_alt', label: 'Images with alt text', weight: 4, status, tip });
    }
  }

  // ————————————————————————————————————————————
  // Internal & external links (blogs only)
  // ————————————————————————————————————————————
  if (preset.checkInternalLinks) {
    const links = html.match(/<a\b[^>]*href\s*=\s*"([^"]+)"/gi) || [];
    const hrefs = links.map(a => (a.match(/href\s*=\s*"([^"]+)"/i) || [])[1] || '');
    const internal = hrefs.filter(h => h.startsWith('/') || h.includes('indusfact.com'));
    const external = hrefs.filter(h => /^https?:\/\//i.test(h) && !h.includes('indusfact.com'));
    checks.push({
      key: 'internal_links', label: 'Internal links', weight: 4,
      status: internal.length >= 2 ? 'pass' : (internal.length === 1 ? 'warn' : 'fail'),
      tip: internal.length >= 2 ? `${internal.length} internal links — good link equity flow.` : 'Link to at least 2 related IndusFact pages.',
    });
    checks.push({
      key: 'external_links', label: 'External link (authority)', weight: 3,
      status: external.length >= 1 ? 'pass' : 'warn',
      tip: external.length >= 1 ? `${external.length} external link${external.length > 1 ? 's' : ''} to authoritative sources.` : 'Cite at least one authoritative external source.',
    });
  }

  // ————————————————————————————————————————————
  // Readability (avg sentence length)
  // ————————————————————————————————————————————
  if (words >= 50) {
    const asl = avgSentenceLength(bodyText);
    let status = 'pass';
    let tip = `Average sentence: ${asl} words — easy to read.`;
    if (asl > 25) { status = 'fail'; tip = `Sentences average ${asl} words — break long sentences up.`; }
    else if (asl > 20) { status = 'warn'; tip = `Sentences average ${asl} words — a bit long.`; }
    else if (asl > 0 && asl < 6) { status = 'warn'; tip = `Sentences average ${asl} words — feels choppy.`; }
    checks.push({ key: 'readability', label: 'Readability (sentence length)', weight: 4, status, tip });
  }

  // ————————————————————————————————————————————
  // OG image (crucial for social CTR)
  // Counts the EFFECTIVE OG image: a manual pick, or the primary content
  // image derived live when none was set. Same resolver the public og:image
  // tags use, so the score reflects the real social preview.
  // ————————————————————————————————————————————
  const effectiveOg = resolveEffectiveOgImage(values, contentType);
  checks.push({
    key: 'og_image', label: 'Social share image (OG)', weight: 3,
    status: effectiveOg ? 'pass' : 'warn',
    tip: values.og_image_url
      ? 'Custom social share image set.'
      : (effectiveOg
        ? 'Using the primary content image for social previews. Upload a dedicated OG image (1200×630) to override.'
        : 'Add an OG image (1200×630) for social previews.'),
  });

  // ————————————————————————————————————————————
  // Compute weighted score
  // ————————————————————————————————————————————
  const applied = checks.filter(c => c.status !== 'na');
  const totalWeight = applied.reduce((s, c) => s + c.weight, 0);
  const earned = applied.reduce((s, c) => s + c.weight * (c.status === 'pass' ? 1 : c.status === 'warn' ? 0.5 : 0), 0);
  const score = totalWeight === 0 ? 0 : Math.round((earned / totalWeight) * 100);

  return { score, checks, totalWeight, earned };
};

// ————————————————————————————————————————————
// Presentational component
// ————————————————————————————————————————————
const scoreBand = (s) => {
  if (s >= 90) return { label: 'Excellent', color: 'text-emerald-700', ring: 'ring-emerald-500/30', bg: 'bg-emerald-500', track: 'bg-emerald-100', text: 'text-emerald-800', border: 'border-emerald-300' };
  if (s >= 75) return { label: 'Good', color: 'text-lime-700', ring: 'ring-lime-500/30', bg: 'bg-lime-500', track: 'bg-lime-100', text: 'text-lime-800', border: 'border-lime-300' };
  if (s >= 50) return { label: 'Needs work', color: 'text-amber-700', ring: 'ring-amber-500/30', bg: 'bg-amber-500', track: 'bg-amber-100', text: 'text-amber-800', border: 'border-amber-300' };
  return { label: 'Poor', color: 'text-red-700', ring: 'ring-red-500/30', bg: 'bg-red-500', track: 'bg-red-100', text: 'text-red-800', border: 'border-red-300' };
};

const StatusIcon = ({ status }) => {
  if (status === 'pass') return <CheckCircle2 className='h-4 w-4 text-emerald-600 shrink-0' />;
  if (status === 'warn') return <AlertTriangle className='h-4 w-4 text-amber-600 shrink-0' />;
  return <XCircle className='h-4 w-4 text-red-600 shrink-0' />;
};

export const SEOScoreCard = ({ values = {}, contentType = 'page', className = '' }) => {
  const { score, checks } = useMemo(() => computeSeoScore(values, contentType), [values, contentType]);
  const band = scoreBand(score);

  const passed = checks.filter(c => c.status === 'pass').length;
  const warned = checks.filter(c => c.status === 'warn').length;
  const failed = checks.filter(c => c.status === 'fail').length;

  // Group actionable items (warn + fail) to surface top improvements.
  const actionable = checks.filter(c => c.status !== 'pass');

  return (
    <div
      className={`rounded-lg border bg-card p-4 ${className}`}
      data-testid='seo-score-card'
    >
      <div className='flex items-center justify-between gap-4 mb-3'>
        <div className='flex items-center gap-2'>
          <Gauge className='h-4 w-4 text-primary' />
          <div className='font-semibold text-sm'>SEO Score <span className='text-[10px] font-normal text-muted-foreground uppercase tracking-wider ml-1'>Staff only</span></div>
        </div>
        <div className='flex items-center gap-3'>
          <div className='text-right'>
            <div className={`text-2xl font-bold leading-none ${band.color}`} data-testid='seo-score-value'>{score}</div>
            <div className={`text-[10px] font-semibold uppercase tracking-wider ${band.color}`}>{band.label}</div>
          </div>
          <div className='relative h-14 w-14 shrink-0'>
            <svg viewBox='0 0 36 36' className='h-14 w-14 -rotate-90'>
              <circle cx='18' cy='18' r='16' fill='none' className='stroke-muted' strokeWidth='3' />
              <circle
                cx='18' cy='18' r='16' fill='none'
                className={band.color}
                strokeWidth='3'
                strokeLinecap='round'
                strokeDasharray={`${(score / 100) * 100.53} 100.53`}
                stroke='currentColor'
                style={{ transition: 'stroke-dasharray 300ms ease' }}
              />
            </svg>
            <div className='absolute inset-0 flex items-center justify-center'>
              <TrendingUp className={`h-4 w-4 ${band.color}`} />
            </div>
          </div>
        </div>
      </div>

      {/* Progress bar for a quick scan */}
      <div className={`h-1.5 rounded-full overflow-hidden ${band.track} mb-3`}>
        <div
          className={`h-full ${band.bg} transition-all duration-300 ease-out`}
          style={{ width: `${score}%` }}
        />
      </div>

      {/* Summary chips */}
      <div className='flex flex-wrap gap-1.5 mb-3'>
        <span className='inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-emerald-300 bg-emerald-50 text-emerald-800' data-testid='seo-score-passed'>
          <CheckCircle2 className='h-3 w-3' /> {passed} passed
        </span>
        <span className='inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-amber-300 bg-amber-50 text-amber-800' data-testid='seo-score-warned'>
          <AlertTriangle className='h-3 w-3' /> {warned} warnings
        </span>
        <span className='inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-red-300 bg-red-50 text-red-800' data-testid='seo-score-failed'>
          <XCircle className='h-3 w-3' /> {failed} to fix
        </span>
      </div>

      {/* Actionable improvements first, then passed checks */}
      {actionable.length > 0 && (
        <div className='space-y-1.5' data-testid='seo-score-actionable'>
          <div className='text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1'>
            Top improvements
          </div>
          {actionable.map((c) => (
            <div key={c.key} className='flex items-start gap-2 text-xs' data-testid={`seo-check-${c.key}`}>
              <StatusIcon status={c.status} />
              <div className='flex-1'>
                <div className='font-medium text-foreground'>{c.label}</div>
                <div className='text-muted-foreground'>{c.tip}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Passed checks in a collapsed line (visible for reassurance) */}
      {passed > 0 && (
        <details className='mt-3'>
          <summary className='text-[11px] uppercase tracking-wider text-muted-foreground font-semibold cursor-pointer hover:text-foreground' data-testid='seo-score-passed-toggle'>
            View {passed} passed check{passed > 1 ? 's' : ''}
          </summary>
          <div className='space-y-1.5 mt-2'>
            {checks.filter(c => c.status === 'pass').map(c => (
              <div key={c.key} className='flex items-start gap-2 text-xs' data-testid={`seo-check-${c.key}`}>
                <StatusIcon status={c.status} />
                <div className='flex-1'>
                  <div className='font-medium text-foreground'>{c.label}</div>
                  <div className='text-muted-foreground'>{c.tip}</div>
                </div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
};

export default SEOScoreCard;
